import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../layouts/MainLayout';
import {
  checkItemService,
  type CategoryInfo,
} from '../../services/api/checkItemService';
import type { CheckItem, MAMethod } from '../../types';


const priorityConfig: Record<'high' | 'medium' | 'low', { label: string; color: 'error' | 'warning' | 'success' }> = {
  high: { label: '高', color: 'error' },
  medium: { label: '中', color: 'warning' },
  low: { label: '低', color: 'success' },
};

const maMethodLabels: Record<MAMethod, string> = {
  stock_transfer: '株式譲渡',
  business_transfer: '事業譲渡',
  merger: '合併',
  company_split: '会社分割',
  stock_exchange: '株式交換・移転',
};

export function MasterDataPage() {
  // カテゴリとアイテムの状態
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [checkItems, setCheckItems] = useState<CheckItem[]>([]);

  // ローディング状態
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(true);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ダイアログ状態
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CheckItem | null>(null);

  // メッセージ状態
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // フォーム状態
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
    maMethod: 'all' as string,
  });

  // カテゴリ一覧取得
  const fetchCategories = useCallback(async () => {
    setIsCategoriesLoading(true);
    try {
      const data = await checkItemService.getCategories();
      setCategories(data);
      // 最初のカテゴリを選択
      if (data.length > 0 && !selectedCategoryId) {
        setSelectedCategoryId(data[0].id);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'カテゴリの取得に失敗しました',
      });
    } finally {
      setIsCategoriesLoading(false);
    }
  }, [selectedCategoryId]);

  // チェック項目一覧取得
  const fetchCheckItems = useCallback(async (categoryId: string) => {
    setIsItemsLoading(true);
    try {
      const data = await checkItemService.getCheckItems({ categoryId });
      setCheckItems(data);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'チェック項目の取得に失敗しました',
      });
      setCheckItems([]);
    } finally {
      setIsItemsLoading(false);
    }
  }, []);

  // 初回ロード時にカテゴリ一覧を取得
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // カテゴリ選択時にチェック項目を取得
  useEffect(() => {
    if (selectedCategoryId) {
      fetchCheckItems(selectedCategoryId);
    }
  }, [selectedCategoryId, fetchCheckItems]);

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
  const selectedCategoryName = selectedCategory?.name || '';

  const handleOpenAddDialog = () => {
    setSelectedItem(null);
    setFormData({
      name: '',
      description: '',
      priority: 'medium',
      maMethod: 'all',
    });
    setEditDialogOpen(true);
  };

  const handleOpenEditDialog = (item: CheckItem) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      priority: item.priority,
      maMethod: item.maMethod,
    });
    setEditDialogOpen(true);
  };

  const handleOpenDeleteDialog = (item: CheckItem) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !selectedCategoryId) return;

    setIsSubmitting(true);
    try {
      if (selectedItem) {
        // 既存アイテムの更新
        const updated = await checkItemService.updateCheckItem(selectedItem.id, {
          name: formData.name,
          description: formData.description || undefined,
          priority: formData.priority,
          maMethod: formData.maMethod as MAMethod | 'all',
        });
        setCheckItems((prev) =>
          prev.map((item) => (item.id === selectedItem.id ? updated : item))
        );
        setMessage({ type: 'success', text: `${formData.name} を更新しました` });
      } else {
        // 新規アイテムの作成
        const created = await checkItemService.createCheckItem({
          categoryId: selectedCategoryId,
          name: formData.name,
          description: formData.description || undefined,
          priority: formData.priority,
          maMethod: formData.maMethod as MAMethod | 'all',
        });
        setCheckItems((prev) => [...prev, created]);
        // カテゴリの項目数を更新
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === selectedCategoryId
              ? { ...cat, itemCount: cat.itemCount + 1 }
              : cat
          )
        );
        setMessage({ type: 'success', text: `${formData.name} を追加しました` });
      }
      setEditDialogOpen(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '保存に失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    setIsSubmitting(true);
    try {
      await checkItemService.deleteCheckItem(selectedItem.id);
      setCheckItems((prev) => prev.filter((item) => item.id !== selectedItem.id));
      // カテゴリの項目数を更新
      if (selectedCategoryId) {
        setCategories((prev) =>
          prev.map((cat) =>
            cat.id === selectedCategoryId
              ? { ...cat, itemCount: Math.max(0, cat.itemCount - 1) }
              : cat
          )
        );
      }
      setMessage({ type: 'success', text: `${selectedItem.name} を削除しました` });
      setDeleteDialogOpen(false);
      setSelectedItem(null);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : '削除に失敗しました',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMaMethodDisplay = (maMethod: MAMethod | 'all') => {
    if (maMethod === 'all') return '全て';
    return maMethodLabels[maMethod];
  };

  return (
    <MainLayout>
      <Box>
        {/* Page Title */}
        <Typography variant="h5" component="h1" fontWeight={500} sx={{ mb: 3 }}>
          マスターデータ管理
        </Typography>

        {/* Message */}
        {message && (
          <Alert
            severity={message.type}
            sx={{ mb: 3 }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
        )}

        {/* Two Column Layout */}
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left Column: Category List */}
          <Paper sx={{ width: 280, flexShrink: 0 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle1" fontWeight={500}>
                リスクカテゴリ
              </Typography>
            </Box>
            <List disablePadding>
              {isCategoriesLoading ? (
                // カテゴリローディング状態
                Array.from({ length: 8 }).map((_, index) => (
                  <Box key={index} sx={{ p: 2 }}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="text" width="30%" height={16} />
                  </Box>
                ))
              ) : categories.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    カテゴリがありません
                  </Typography>
                </Box>
              ) : (
                categories.map((category) => (
                  <ListItemButton
                    key={category.id}
                    selected={selectedCategoryId === category.id}
                    onClick={() => setSelectedCategoryId(category.id)}
                    sx={{
                      borderLeft: selectedCategoryId === category.id ? 3 : 0,
                      borderLeftColor: 'primary.main',
                    }}
                  >
                    <ListItemText
                      primary={category.name}
                      secondary={`${category.itemCount}項目`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                ))
              )}
            </List>
          </Paper>

          {/* Right Column: Check Items */}
          <Paper sx={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1" fontWeight={500}>
                {selectedCategoryName || (isCategoriesLoading ? <Skeleton width={100} /> : 'カテゴリを選択')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                size="small"
                onClick={handleOpenAddDialog}
                disabled={!selectedCategoryId || isCategoriesLoading}
              >
                項目を追加
              </Button>
            </Box>

            {/* Check Items List */}
            <List disablePadding>
              {isItemsLoading ? (
                // アイテムローディング状態
                Array.from({ length: 4 }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      borderBottom: index < 3 ? 1 : 0,
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                    }}
                  >
                    <Skeleton variant="rectangular" width={24} height={24} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="70%" />
                      <Skeleton variant="text" width="90%" />
                      <Skeleton variant="text" width="30%" height={20} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Skeleton variant="circular" width={28} height={28} />
                      <Skeleton variant="circular" width={28} height={28} />
                    </Box>
                  </Box>
                ))
              ) : checkItems.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {selectedCategoryId
                      ? 'このカテゴリにはチェック項目がありません'
                      : 'カテゴリを選択してください'}
                  </Typography>
                </Box>
              ) : (
                checkItems.map((item, index) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 2,
                      borderBottom: index < checkItems.length - 1 ? 1 : 0,
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {/* Drag Handle */}
                    <DragIcon
                      sx={{ color: 'text.secondary', cursor: 'grab', mt: 0.5 }}
                    />

                    {/* Content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {item.name}
                        </Typography>
                        <Chip
                          label={priorityConfig[item.priority].label}
                          color={priorityConfig[item.priority].color}
                          size="small"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </Box>
                      {item.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {item.description}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={getMaMethodDisplay(item.maMethod)}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditDialog(item)}
                        title="編集"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(item)}
                        title="削除"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              )}
            </List>
          </Paper>
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedItem ? 'チェック項目を編集' : 'チェック項目を追加'}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="項目名"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            fullWidth
            required
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="説明"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>対象M&A手法</InputLabel>
            <Select
              value={formData.maMethod}
              label="対象M&A手法"
              onChange={(e) => setFormData({ ...formData, maMethod: e.target.value })}
            >
              <MenuItem value="all">全て</MenuItem>
              <MenuItem value="stock_transfer">株式譲渡</MenuItem>
              <MenuItem value="business_transfer">事業譲渡</MenuItem>
              <MenuItem value="merger">合併</MenuItem>
              <MenuItem value="company_split">会社分割</MenuItem>
              <MenuItem value="stock_exchange">株式交換・移転</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>優先度</InputLabel>
            <Select
              value={formData.priority}
              label="優先度"
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value as 'high' | 'medium' | 'low' })
              }
            >
              <MenuItem value="high">高</MenuItem>
              <MenuItem value="medium">中</MenuItem>
              <MenuItem value="low">低</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSubmitting || !formData.name}
          >
            {isSubmitting ? <CircularProgress size={24} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>チェック項目の削除</DialogTitle>
        <DialogContent>
          <Typography>
            「{selectedItem?.name}」を削除してもよろしいですか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
